import { useEffect } from "react";
import { Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";
import { LABELS } from "~/resources/Labels";
import { MESSAGES } from "~/resources/Message";
import { FormCloudProvisionerTemplateResponse, OptionProp } from "~/models/Commons ";
import { SIMPLInput } from "~/components/commons/input/SIMPLInput";
import { SIMPLDialog } from "~/components/commons/dialog/SimplDialog";
import { SIMPLSelectDropdown } from "~/components/commons/input/SIMPLSelectDropdown";
import { SIMPLButtonCustomized } from "~/components/commons/button/SIMPLButtonCustumized";
import { UseFormCloudProvisionerTemplate } from "./UseFormCloudProvisionerTemplate";

const AZURE_VM_SIZES = [
  { label: "Standard_B2s (2 vCPU, 4 GB)", value: "Standard_B2s" },
  { label: "Standard_B4ms (4 vCPU, 16 GB)", value: "Standard_B4ms" },
  { label: "Standard_D2s_v3 (2 vCPU, 8 GB)", value: "Standard_D2s_v3" },
  { label: "Standard_D4s_v3 (4 vCPU, 16 GB)", value: "Standard_D4s_v3" },
  { label: "Standard_D8s_v3 (8 vCPU, 32 GB)", value: "Standard_D8s_v3" },
];

const AZURE_LOCATIONS = [
  { label: "North Europe (northeurope)", value: "northeurope" },
  { label: "West Europe (westeurope)", value: "westeurope" },
  { label: "East US (eastus)", value: "eastus" },
  { label: "West US 2 (westus2)", value: "westus2" },
  { label: "UK South (uksouth)", value: "uksouth" },
  { label: "Germany West Central (germanywestcentral)", value: "germanywestcentral" },
];

const OS_OPTIONS = [
  { label: "Ubuntu 22.04", value: "Ubuntu-22.04" },
  { label: "Ubuntu 24.04", value: "Ubuntu-24.04" },
  { label: "CentOS 8", value: "CentOS-8" },
];

const PLACEHOLDER_URNS: Record<string, string> = {
  "Ubuntu-22.04": "Canonical:0001-com-ubuntu-server-jammy:22_04-lts-gen2:latest",
  "Ubuntu-24.04": "Canonical:0001-com-ubuntu-server-focal:20_04-lts-gen2:latest",
  "CentOS-8": "OpenLogic:CentOS:8_5-gen2:latest",
};

const PLACEHOLDER_AMIS: Record<string, string> = {
  "Ubuntu-22.04": "ami-0c02fb55956c7d316",
  "Ubuntu-24.04": "ami-0866a3c8686eaeeba",
  "CentOS-8": "ami-0427090fd1714168b",
};

const PROVIDER_OPTIONS = [
  { label: "Azure", value: "azure" },
  { label: "AWS", value: "aws" },
];

interface Props {
  onCloseModal: () => void;
  editedItem: FormCloudProvisionerTemplateResponse;
  refetch: () => void;
  title?: string;
}

export const EditCloudProvisionerTemplate = ({ onCloseModal, editedItem, refetch, title }: Props) => {
  const {
    handleSubmit, control, errors, updateForm, setValue, watch,
    fields, append, remove, replace,
    awsAmiFields, awsAmiAppend, awsAmiRemove, awsAmiReplace,
  } = UseFormCloudProvisionerTemplate(onCloseModal, refetch, editedItem?.id);

  useEffect(() => {
    if (editedItem?.id) {
      setValue("fileName", editedItem.fileName ?? "");
      setValue("providerType", editedItem.isAws ? "aws" : "azure");
      setValue("azureVmSizes", editedItem.azureVmSizes ?? []);
      setValue("azureLocation", editedItem.azureLocation ?? "");
      setValue("azureResourceGroup", editedItem.azureResourceGroup ?? "");
      setValue("azureAppPorts", editedItem.azureAppPorts ?? []);
      replace(
        Object.entries(editedItem.osImageUrns ?? {}).map(([os, urn]) => ({ os, urn }))
      );
      setValue("awsInstanceTypes", editedItem.awsInstanceTypes ?? []);
      setValue("awsRegion", editedItem.awsRegion ?? "");
      setValue("awsAppPorts", editedItem.awsAppPorts ?? []);
      awsAmiReplace(
        Object.entries(editedItem.awsAmiIds ?? {}).map(([os, amiId]) => ({ os, amiId }))
      );
    }
  }, [editedItem, setValue, replace, awsAmiReplace]);

  const providerType = watch("providerType");
  const isAzure = providerType === "azure";
  const isAws = providerType === "aws";

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    handleSubmit(updateForm)(event);
  };

  const renderButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized label={LABELS.Cancel} secondary buttonHandlerAction={onCloseModal} />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized label={LABELS.Update} primary />
      </div>
    </>
  );

  return (
    <SIMPLDialog
      onCloseModal={onCloseModal}
      title={title ?? LABELS.CloudProvisionerTemplateManagement}
      actions={renderButtons()}
      onSubmit={handleFormSubmit}
    >
      <div className="flex justify-center">
        <div className="w-[500px]">
          <div className="flex flex-col w-full gap-y-4">
            <SIMPLInput
              name="fileName"
              control={control}
              rules={{ required: { value: true, message: LABELS.NameRequired } }}
              label={LABELS.Name}
              placeholder={LABELS.Name}
              required
              id="fileName"
              fontWeight="bold"
              errorLabel={errors.fileName?.message}
            />

            {/* Provider Type */}
            <div className="h-auto">
              <Controller
                name="providerType"
                control={control}
                rules={{ required: MESSAGES.MessageProviderTypeRequired }}
                render={({ field }) => (
                  <SIMPLSelectDropdown
                    id="providerType"
                    label={LABELS.ProviderType}
                    options={PROVIDER_OPTIONS}
                    isRequired
                    value={PROVIDER_OPTIONS.find((o) => o.value === field.value) ?? null}
                    onChange={(selected: any) => field.onChange(selected?.value ?? "")}
                    hideErrorLabel
                    errorLabel={errors.providerType?.message as string}
                    placeholder={LABELS.ProviderType}
                  />
                )}
              />
            </div>

            {/* Azure fields */}
            {isAzure && (
              <>
                {/* OS + URN pairs */}
                <div className="flex flex-col gap-y-2">
                  <div className="font-semibold text-sm">
                    {LABELS.SelectOs} <span className="text-red-500">*</span>
                  </div>
                  {OS_OPTIONS.map((option) => {
                    const idx = fields.findIndex((f) => f.os === option.value);
                    const checked = idx !== -1;
                    return (
                      <div key={option.value} className="flex flex-col gap-y-1">
                        <label className="flex items-center gap-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              e.target.checked
                                ? append({ os: option.value, urn: "" })
                                : remove(idx)
                            }
                          />
                          {option.label}
                        </label>
                        {checked && (
                          <SIMPLInput
                            name={`osUrnPairs.${idx}.urn`}
                            control={control}
                            rules={{ required: { value: true, message: MESSAGES.MessageAzureImageUrnRequired } }}
                            label={LABELS.AzureImageUrn}
                            placeholder={PLACEHOLDER_URNS[option.value] ?? "Publisher:Offer:SKU:Version"}
                            id={`urn-${option.value}`}
                            fontWeight="bold"
                            errorLabel={(errors.osUrnPairs as any)?.[idx]?.urn?.message}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Azure VM Size */}
                <div className="h-auto">
                  <Controller
                    name="azureVmSizes"
                    control={control}
                    rules={{ required: MESSAGES.MessageAzureVmSizeRequired }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="azureVmSize"
                        label={LABELS.AzureVmSize}
                        options={AZURE_VM_SIZES}
                        isRequired
                        isMulti={true}
                        value={AZURE_VM_SIZES.filter((o) => field.value?.includes(o.value))}
                        onChange={(selected: any) =>
                          field.onChange((selected as OptionProp[] | null)?.map((o) => o.value) ?? [])
                        }
                        hideErrorLabel
                        errorLabel={errors.azureVmSizes?.message as string}
                        placeholder={LABELS.AzureVmSize}
                      />
                    )}
                  />
                </div>

                {/* Azure Location */}
                <div className="h-auto">
                  <Controller
                    name="azureLocation"
                    control={control}
                    rules={{ required: MESSAGES.MessageAzureLocationRequired }}
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="azureLocation"
                        label={LABELS.AzureLocation}
                        options={AZURE_LOCATIONS}
                        isRequired
                        value={AZURE_LOCATIONS.find((o) => o.value === field.value) ?? null}
                        onChange={(selected: any) => field.onChange(selected?.value ?? "")}
                        hideErrorLabel
                        errorLabel={errors.azureLocation?.message as string}
                        placeholder={LABELS.AzureLocation}
                      />
                    )}
                  />
                </div>

                <SIMPLInput
                  name="azureResourceGroup"
                  control={control}
                  rules={{ required: { value: true, message: MESSAGES.MessageAzureResourceGroupRequired } }}
                  label={LABELS.AzureResourceGroup}
                  placeholder={LABELS.AzureResourceGroup}
                  required
                  id="azureResourceGroup"
                  fontWeight="bold"
                  errorLabel={errors.azureResourceGroup?.message}
                />

                {/* Azure App Ports */}
                <div>
                  <label className="inline-block cursor-pointer text-sm font-bold mb-1">
                    {LABELS.AzureAppPorts}
                  </label>
                  <Controller
                    name="azureAppPorts"
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        isMulti
                        placeholder={LABELS.AzureAppPorts + " (e.g. 8088, 443)"}
                        noOptionsMessage={() => "Type a port and press Enter"}
                        value={(field.value ?? []).map((p: string) => ({ label: p, value: p }))}
                        onChange={(selected) =>
                          field.onChange(selected ? selected.map((o) => String(o.value)) : [])
                        }
                        styles={{
                          control: (base) => ({ ...base, backgroundColor: "#F5F5F5", minHeight: "44px" }),
                        }}
                      />
                    )}
                  />
                </div>
              </>
            )}

            {/* AWS fields */}
            {isAws && (
              <>
                {/* OS + AMI ID pairs */}
                <div className="flex flex-col gap-y-2">
                  <div className="font-semibold text-sm">
                    {LABELS.AwsAmiIds} <span className="text-red-500">*</span>
                  </div>
                  {OS_OPTIONS.map((option) => {
                    const idx = awsAmiFields.findIndex((f) => f.os === option.value);
                    const checked = idx !== -1;
                    return (
                      <div key={option.value} className="flex flex-col gap-y-1">
                        <label className="flex items-center gap-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) =>
                              e.target.checked
                                ? awsAmiAppend({ os: option.value, amiId: "" })
                                : awsAmiRemove(idx)
                            }
                          />
                          {option.label}
                        </label>
                        {checked && (
                          <SIMPLInput
                            name={`awsAmiPairs.${idx}.amiId`}
                            control={control}
                            rules={{ required: { value: true, message: MESSAGES.MessageAwsAmiIdsRequired } }}
                            label={LABELS.AwsAmiIds}
                            placeholder={PLACEHOLDER_AMIS[option.value] ?? "ami-xxxxxxxxxxxxxxxxx"}
                            id={`ami-${option.value}`}
                            fontWeight="bold"
                            errorLabel={(errors.awsAmiPairs as any)?.[idx]?.amiId?.message}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* AWS Instance Types */}
                <div className="h-auto">
                  <Controller
                    name="awsInstanceTypes"
                    control={control}
                    rules={{ required: MESSAGES.MessageAwsInstanceTypesRequired }}
                    render={({ field }) => (
                      <CreatableSelect
                        isMulti
                        placeholder={LABELS.AwsInstanceTypes + " (e.g. t3.medium)"}
                        noOptionsMessage={() => "Type an instance type and press Enter"}
                        value={(field.value ?? []).map((v: string) => ({ label: v, value: v }))}
                        onChange={(selected) =>
                          field.onChange(selected ? selected.map((o) => String(o.value)) : [])
                        }
                        styles={{
                          control: (base) => ({ ...base, backgroundColor: "#F5F5F5", minHeight: "44px" }),
                        }}
                      />
                    )}
                  />
                  <label className="inline-block cursor-pointer text-sm font-bold mb-1">
                    {LABELS.AwsInstanceTypes} <span className="text-red-500">*</span>
                  </label>
                </div>

                {/* AWS Region */}
                <SIMPLInput
                  name="awsRegion"
                  control={control}
                  rules={{ required: { value: true, message: MESSAGES.MessageAwsRegionRequired } }}
                  label={LABELS.AwsRegion}
                  placeholder="eu-west-1"
                  required
                  id="awsRegion"
                  fontWeight="bold"
                  errorLabel={errors.awsRegion?.message}
                />

                {/* AWS App Ports */}
                <div>
                  <label className="inline-block cursor-pointer text-sm font-bold mb-1">
                    {LABELS.AwsAppPorts}
                  </label>
                  <Controller
                    name="awsAppPorts"
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        isMulti
                        placeholder={LABELS.AwsAppPorts + " (e.g. 8088, 443)"}
                        noOptionsMessage={() => "Type a port and press Enter"}
                        value={(field.value ?? []).map((p: string) => ({ label: p, value: p }))}
                        onChange={(selected) =>
                          field.onChange(selected ? selected.map((o) => String(o.value)) : [])
                        }
                        styles={{
                          control: (base) => ({ ...base, backgroundColor: "#F5F5F5", minHeight: "44px" }),
                        }}
                      />
                    )}
                  />
                </div>
              </>
            )}

          </div>
        </div>
      </div>
    </SIMPLDialog>
  );
};
